import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

import { Form, FormSpy } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { Unstable_Grid2 as Grid } from '@mui/material';
import PlanFields from 'components/plan/PlanFields';
import PlanPreview from 'components/plan/PlanPreview';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryLoadingButton from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultPlanFormValidations from 'common/validations/form/defaultPlan';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import { updatePlanInitialValues } from 'common/form/initial-values/update';
import { getFieldsToUpdate } from 'common/form/utils';
import getDataToSave from 'common/form/dataToSave';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import planSlice from 'store/modules/plan';
import plan from 'api/graphql/plan';

const requiredFields = ['name', 'price', 'sessions', 'payment_link'];

function PlanEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['buttons', 'routes', 'errors']);
  const pageTitle = t('plan.edit', { ns: 'routes' });
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const currentPlan = useSelector((state) => state.plan.currentPlan);
  const status = useSelector((state) => state.plan.status);
  const previousStatus = React.useRef(status);

  useEffect(() => {
    dispatch(planSlice.actions.find(id));
  }, []);

  useEffect(() => {
    if (currentPlan && Object.keys(currentPlan).length > 0 && currentPlan.id === id) {
      const planData = updatePlanInitialValues(currentPlan);
      setInitialValues(planData);
    }
  }, [currentPlan, id]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista después de una edición exitosa
  useEffect(() => {
    if (previousStatus.current === requestStatus.loading && status === requestStatus.success && isUpdating) {
      setIsUpdating(false);
      navigate(routes.listPlan.path);
    } else if (previousStatus.current === requestStatus.loading && status === requestStatus.error && isUpdating) {
      setIsUpdating(false);
    }
    previousStatus.current = status;
  }, [status, isUpdating, navigate]);

  const onEdit = useCallback(
    async (values) => {
      setIsUpdating(true);
      const { sessionTypeIds, image, ...planData } = values;
      const { sessionTypeIds: _, image: _img, ...initialValuesWithoutCategories } = initialValues;

      if (image && image instanceof File) {
        try {
          await plan.uploadImage(id, image);
        } catch {
          dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
            key: `plan-image-upload-error-${Date.now()}`,
            variant: 'error',
            message: t('upload.size.image', { ns: 'errors' })
          })));
          setIsUpdating(false);
          return;
        }
      } else if (image === null && initialValues.image) {
        try {
          await plan.removeImage(id);
        } catch {
          // fallo silencioso — el plan se actualiza igual
        }
      }

      const fieldsToUpdate = getFieldsToUpdate(planData, initialValuesWithoutCategories);
      const dataToSave = getDataToSave(fieldsToUpdate, 'plan');
      dispatch(planSlice.actions.update({ id, input: dataToSave }));

      if (sessionTypeIds !== undefined) {
        try {
          await plan.syncSessionTypes(id, sessionTypeIds);
        } catch {
          // Categories sync failed silently — plan data was already saved
        }
      }
    },
    [dispatch, id, initialValues, t]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        key={Object.keys(initialValues).length > 0 ? currentPlan?.id : 'loading'}
        initialValues={initialValues}
        onSubmit={onEdit}
        validate={defaultPlanFormValidations}
        render={({ handleSubmit, invalid, pristine }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid xs={12} lg={7}>
                <PlanFields requiredFields={requiredFields} />
              </Grid>
              <Grid xs={12} lg={5}>
                <FormSpy subscription={{ values: true }}>
                  {({ values }) => <PlanPreview values={values} />}
                </FormSpy>
              </Grid>
            </Grid>
            <StyledButtonsContainer>
              <SecondaryLoadingButton
                component={RouterLink}
                disabled={status === requestStatus.loading || isUpdating}
                style={{ marginRight: '32px' }}
                label={t('cancel', { ns: 'buttons' })}
                to={routes.listPlan.path}
                type="button"
              />
              <PrimaryLoadingButton
                disabled={invalid || pristine || status === requestStatus.loading || isUpdating}
                loading={status === requestStatus.loading || isUpdating}
                label={t('plan.edit', { ns: 'buttons' })}
                type="submit"
              />
            </StyledButtonsContainer>
          </form>
        )}
      />
    </>
  );
}

export default PlanEdit;
